import React from 'react'
import { Box, Flex, Text, Label, LabelVariant } from '@workwhile/ui'
import { ExternalBankAccount, MarqetaCardStatus } from 'api/payments'
import { CopyableCode } from 'components/Shared/CopyableCode'
import { toTitleCase } from 'lib/string'

interface BankAccountBoxProps {
  account: ExternalBankAccount
  badges: string[]
  /** Shown above the card brand / bank name (e.g. cardholder name). */
  name?: string | null
  /** Expiry shown on the same line as the last4 (e.g. "01/2027"). */
  expiry?: string | null
  /** Routing number shown above the account last4. */
  routingNumber?: string | null
  /** Shown in the top-right corner of the box. */
  id?: string | number | null
}

const getCardStatusVariant = (
  status: MarqetaCardStatus | null
): LabelVariant => {
  if (!status) return 'neutral'
  switch (status.toLowerCase()) {
    case 'active':
      return 'success'
    case 'suspended':
    case 'terminated':
      return 'error'
    case 'limited':
    case 'unactivated':
      return 'warning'
    default:
      return 'neutral'
  }
}

const formatAccountType = (accountType: string) =>
  toTitleCase(accountType.toLowerCase())

export const BankAccountBox = ({
  account,
  badges,
  name,
  expiry,
  routingNumber,
  id,
}: BankAccountBoxProps) => {
  const isBankAccount = Boolean(routingNumber)

  return (
    <Box
      key={account.displayName}
      bg="white"
      border="1px solid"
      borderColor="border"
      borderRadius="small"
      p={2}
      mb={2}
      style={{ position: 'relative' }}
    >
      {id != null ? (
        <Flex
          alignItems="center"
          gap={1}
          style={{ position: 'absolute', top: 8, right: 8 }}
        >
          <Text fontSize={1} color="lightText">
            ID:
          </Text>
          <CopyableCode value={String(id)} />
        </Flex>
      ) : null}

      <Box pr={id != null ? '72px' : 0} mb={2}>
        <Text fontSize={1} fontWeight="bold">
          {account.displayName || 'Unknown Bank'}
        </Text>
        {name ? (
          <Text fontSize={1} color="lightText" mt={1}>
            {name}
          </Text>
        ) : null}
      </Box>

      <Flex
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        mb={2}
      >
        <Box flex={1} minWidth={0}>
          {routingNumber ? (
            <Text fontSize={1} mb={1}>
              <Text fontWeight="bold" inline>
                Routing number:
              </Text>{' '}
              •••• {routingNumber.slice(-4)}
            </Text>
          ) : null}
          {account.last4 ? (
            <Flex alignItems="center" gap={2} flexWrap="wrap">
              {isBankAccount ? (
                <Text fontSize={1}>
                  <Text fontWeight="bold" inline>
                    Account number:
                  </Text>{' '}
                  •••• {account.last4}
                </Text>
              ) : (
                <Text fontSize={1}>•••• {account.last4}</Text>
              )}
              {expiry ? (
                <Text fontSize={1} color="lightText">
                  Expires {expiry}
                </Text>
              ) : null}
            </Flex>
          ) : null}
        </Box>
        {account.accountType ? (
          <Label variant="neutral" size="small">
            {formatAccountType(account.accountType)}
          </Label>
        ) : null}
      </Flex>

      {badges.length > 0 ? (
        <Flex flexWrap="wrap" gap={1} mt={1}>
          {badges.map((badge, index) => (
            <Label key={index} size="small">
              {badge}
            </Label>
          ))}
        </Flex>
      ) : null}

      {account.status ? (
        <Flex alignItems="center" gap={1} mb={1}>
          <Text fontSize={1} fontWeight="bold">
            Status:
          </Text>
          <Label variant={getCardStatusVariant(account.status)} size="small">
            {toTitleCase(account.status || 'N/A')}
          </Label>
        </Flex>
      ) : null}
    </Box>
  )
}
